import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { BsCheckCircleFill, BsClockHistory, BsShieldCheck } from 'react-icons/bs';
import { InformePenalLecturaComponent } from 'defcol-caso-general';
import Mensajes from '../../mensajes';
import { MAX_COMENTARIO, MSG_AVISO_REVISION, MSG_CONFIRMA_REVISION } from './constantes';
import {
    alCambiarMonto, alEntrarAlCampo, alSalirDelCampo, armaCuerpoRevision, avisoActualizacion, avisoDecisionPrevia, decisionEsConciliar, montoValido,
    MSG_MONTO_INVALIDO, puedeGuardar, seccion7Inicial, tituloVersion
} from './utilsRevision';

// RG-580 — «Revisión de Informe - Implicado» (tareas 91 y 94): secciones 1 a 6 del informe del abogado en solo lectura
// (con «Modificado» frente a la versión anterior) y sección 7 «Decisión Compañía» editable por el analista.
// `datos` es el objeto del modo `revision` del lambda defensa-fnc-obtiene-informe-penal.
// `onGuardarRevision(cuerpo)` (opcional) guarda la revisión y devuelve { code, message }; sin él el botón no se muestra.
// `onTareaGestionada` se llama cuando el guardado terminó con éxito (code 1).

const sectionHeaderStyle = {
    backgroundColor: '#C94747',
    color: 'white',
    padding: '6px 10px',
    borderRadius: '3px',
    fontWeight: 600,
    fontSize: '14px'
};

const Dato = ({ etiqueta, valor }) => (
    <Col md className='mb-1'>
        <div className='fw-bold' style={{ fontSize: '13px' }}>{etiqueta}</div>
        <div className='text-muted' style={{ fontSize: '13px' }}>{valor === null || valor === undefined || valor === '' ? '—' : valor}</div>
    </Col>
);

const Formulario = ({ datos, onVolver, onHistorial, onGuardarRevision, onTareaGestionada }) => {

    const [decision, setDecision] = useState('');
    const [comentario, setComentario] = useState('');
    const [montos, setMontos] = useState({});

    const [confirmar, setConfirmar] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [guardado, setGuardado] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [responseData, setResponseData] = useState({});
    const enviandoRef = useRef(false);

    useEffect(() => {
        precargaSeccion7();
    }, [datos]);

    // RN-14: la decisión, los montos y el comentario del último informe revisado se precargan para que el analista los refresque.
    const precargaSeccion7 = () => {
        const inicial = seccion7Inicial(datos);
        setDecision(inicial.decision);
        setComentario(inicial.comentario);
        setMontos(inicial.montos);
        setGuardado(false);
        enviandoRef.current = false;
    };

    if (!datos || !datos.informe) return null;

    const combos = datos.combos || {};
    const decisiones = Array.isArray(combos.decisionCompania) ? combos.decisionCompania : [];
    const implicados = Array.isArray(datos.implicados) ? datos.implicados : [];
    const caso = datos.caso || {};
    const aviso = avisoActualizacion(datos.informe, datos.versionAnterior);
    const avisoPrevia = avisoDecisionPrevia(datos.seccion7Previa);
    const mostrarMontos = decisionEsConciliar(combos, decision);
    // Los campos de monto solo cuentan para el guardado mientras el panel está visible (decisión de conciliar).
    const habilitaGuardar = puedeGuardar({ decision, combos, implicados, montos }) && !guardando && !guardado;

    const cambiaMonto = (idImplicado, texto) => setMontos((previo) => alCambiarMonto(previo, idImplicado, texto));
    const entrarMonto = (idImplicado) => setMontos((previo) => alEntrarAlCampo(previo, idImplicado));
    const salirMonto = (idImplicado) => setMontos((previo) => alSalirDelCampo(previo, idImplicado));
    const textoMonto = (idImplicado) => (montos[idImplicado] === undefined ? '$ 0' : montos[idImplicado]);

    // «Guardar Revisión»: primero la confirmación; al confirmar se envía el cuerpo (sin usuario, perfil ni trx).
    // `enviandoRef` es una guarda SÍNCRONA (R1-01): el modal de confirmación sigue clicable durante su transición de salida y el
    // estado de React no se actualiza entre dos clics del mismo ciclo. Tras un éxito no se libera (la revisión ya está guardada);
    // con code 11, -1 o un error de red sí, para poder corregir y reintentar.
    const guardar = async () => {
        if (enviandoRef.current) return;
        enviandoRef.current = true;
        setConfirmar(false);
        setGuardando(true);
        let exito = false;
        try {
            const respuesta = await onGuardarRevision(armaCuerpoRevision({ datos, decision, comentario, montos }));
            setResponseData(respuesta);
            setShowToast(true);
            if (respuesta && respuesta.code === 1) {
                exito = true;
                setGuardado(true);
                if (onTareaGestionada) onTareaGestionada();
            }
        } catch (error) {
            setResponseData({ code: -1, message: error && error.message ? error.message : 'Error al guardar la revisión' });
            setShowToast(true);
        } finally {
            setGuardando(false);
            if (!exito) enviandoRef.current = false;
        }
    };

    // Con éxito, al cerrar el mensaje se cierra el modal; con code 11 o -1 el modal sigue abierto para corregir.
    const handleCloseToast = () => {
        setShowToast(false);
        if (responseData && responseData.code === 1) onVolver();
    };

    return (
        <Fragment>
            {/* Cabecera, datos del caso y pie fijos; solo el cuerpo (el informe y la sección 7) se desplaza (el Modal es `scrollable`). */}
            <Modal.Header style={{ backgroundColor: '#4F517A', color: 'white' }}>
                <Modal.Title as='h6' className='mb-0'>
                    Revisión de Informe - Implicado <small className='ms-2'>{tituloVersion(datos.informe)}</small>
                </Modal.Title>
            </Modal.Header>
            <Container fluid className='border-bottom pt-2 pb-2 bg-light'>
                <Row>
                    <Dato etiqueta='Nro. Siniestro' valor={caso.siniestro} />
                    <Dato etiqueta='Fecha Siniestro' valor={caso.fechaSiniestro} />
                    <Dato etiqueta='Placa Asegurado' valor={caso.placaAsegurado} />
                    <Dato etiqueta='Nombre Asegurado' valor={caso.nombreAsegurado} />
                    <Dato etiqueta='Abogado Inicial' valor={caso.abogadoInicial} />
                </Row>
            </Container>
            <Modal.Body>
                <Alert variant='info' className='py-2' style={{ fontSize: '13px' }}>{MSG_AVISO_REVISION}</Alert>
                {aviso && (
                    <Alert variant='warning' className='py-2' style={{ fontSize: '13px' }}>
                        {aviso.antes}<Badge bg='warning' text='dark'>Modificado</Badge>{aviso.despues}
                    </Alert>
                )}

                <InformePenalLecturaComponent datos={datos} modoRevision />

                {/* Sección 7: Decisión Compañía (editable por el analista) */}
                <div className='mb-2' style={sectionHeaderStyle}>
                    <BsShieldCheck className='me-2' style={{ verticalAlign: '-2px' }} />7. Decisión Compañía <small>(a ingresar por el analista)</small>
                </div>
                <Container fluid className='mb-2 border pt-2 pb-2'>
                    {avisoPrevia && (
                        <Alert variant='secondary' className='py-2' style={{ fontSize: '13px' }}>{avisoPrevia}</Alert>
                    )}
                    <Row>
                        <Col md='5'>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Decisión de la Compañía <span className='text-danger'>(*)</span></Form.Label>
                            <Form.Select size='sm' value={decision} onChange={(e) => setDecision(e.target.value)}>
                                <option value=''>---</option>
                                {decisiones.map((d) => (
                                    <option key={d.id} value={String(d.id)}>{d.glosa}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        {mostrarMontos && (
                            <Col md='7'>
                                <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Montos a Conciliar</Form.Label>
                                <Table bordered size='sm' className='mb-1'>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '50px', backgroundColor: '#4F517A', color: 'white' }}>ID</th>
                                            <th style={{ backgroundColor: '#4F517A', color: 'white' }}>Nombre Implicado</th>
                                            <th style={{ width: '180px', backgroundColor: '#4F517A', color: 'white' }}>Monto a Conciliar</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {implicados.length === 0 && (
                                            <tr><td colSpan='3' className='text-center text-muted'>El caso no tiene implicados.</td></tr>
                                        )}
                                        {implicados.map((i) => (
                                            <tr key={i.idImplicado}>
                                                <td>{i.idImplicado}</td>
                                                <td>{i.nombre}</td>
                                                <td>
                                                    <Form.Control
                                                        size='sm'
                                                        name={`monto-${i.idImplicado}`}
                                                        value={textoMonto(i.idImplicado)}
                                                        maxLength={20}
                                                        isInvalid={!montoValido(textoMonto(i.idImplicado))}
                                                        inputMode='numeric'
                                                        onChange={(e) => cambiaMonto(i.idImplicado, e.target.value)}
                                                        onFocus={() => entrarMonto(i.idImplicado)}
                                                        onBlur={() => salirMonto(i.idImplicado)}
                                                    />
                                                    <Form.Control.Feedback type='invalid'>{MSG_MONTO_INVALIDO}</Form.Control.Feedback>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Col>
                        )}
                    </Row>
                    <Row className='mt-2'>
                        <Col>
                            <Form.Label className='fw-bold' style={{ fontSize: '13px' }}>Comentario / Instrucción al Abogado</Form.Label>
                            <Form.Control
                                as='textarea'
                                name='comentario'
                                rows={3}
                                maxLength={MAX_COMENTARIO}
                                placeholder='Instrucción específica para la gestión del caso...'
                                value={comentario}
                                onChange={(e) => setComentario(e.target.value)}
                            />
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            {/* Zona fija: el aviso del guardado se ve estés donde estés en el formulario. */}
            {showToast && (
                <div style={{ padding: '8px 16px 0' }}>
                    <Mensajes
                        showToast={showToast}
                        handleCloseToast={handleCloseToast}
                        delay={5000}
                        responseData={responseData}
                    />
                </div>
            )}
            <Modal.Footer style={{ backgroundColor: '#f8f9fa' }}>
                {/* Colores e íconos del prototipo (turquesa para Volver e Historial, rojo para Guardar Revisión). Con !important porque el
                    tema global de la app pinta los botones sin variante y un estilo en línea no lo vence. */}
                <style type='text/css'>
                    {`
                    .btn-revision-volver { background-color: #FFFFFF !important; border-color: #5aacb6 !important; color: #5aacb6 !important; }
                    .btn-revision-volver:hover { background-color: #5aacb6 !important; color: #FFFFFF !important; }
                    .btn-revision-historial { background-color: #5aacb6 !important; border-color: #5aacb6 !important; color: #FFFFFF !important; }
                    .btn-revision-historial:hover { background-color: #4c9aa3 !important; border-color: #4c9aa3 !important; }
                    .btn-revision-guardar { background-color: #c94747 !important; border-color: #c94747 !important; color: #FFFFFF !important; }
                    .btn-revision-guardar:hover:not(:disabled) { background-color: #a83737 !important; border-color: #a83737 !important; }
                    .btn-revision-guardar:disabled { opacity: 0.65; }
                    `}
                </style>
                <Button className='btn-revision-volver' onClick={onVolver}>Volver</Button>
                <Button className='btn-revision-historial' onClick={onHistorial}>
                    <BsClockHistory className='me-2' />Historial Informe
                </Button>
                {onGuardarRevision && (
                    <Button className='btn-revision-guardar' disabled={!habilitaGuardar} onClick={() => setConfirmar(true)}>
                        {guardando ? <Spinner as='span' animation='border' size='sm' className='me-2' /> : <BsCheckCircleFill className='me-2' />}
                        {guardando ? 'Guardando…' : 'Guardar Revisión'}
                    </Button>
                )}
            </Modal.Footer>

            {/* Mismo aspecto que los demás modales de confirmación de la app (p. ej. «Confirmar Cierre del Caso»). */}
            <Modal show={confirmar} onHide={() => setConfirmar(false)} size='md'>
                <Modal.Header style={{ backgroundColor: '#4F517A', color: 'white' }}>
                    <Modal.Title as='h6' className='mb-0'>Confirmación</Modal.Title>
                </Modal.Header>
                <Modal.Body>{MSG_CONFIRMA_REVISION}</Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                    <Button className='m-2 w-auto' variant='outline-info' onClick={() => setConfirmar(false)}>Cancelar</Button>
                    <Button className='m-2 w-auto' variant='danger' onClick={guardar}>Confirmar</Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default Formulario;
